import React, { useState, useEffect, useCallback } from 'react';

// Detailed Trailer Plan — forecast-accuracy reconciliation.
// You upload a CSV plan (TrailerID=Vehicle#, Bucket, ExpectedArrivalDate) +
// consumption assumptions. It freezes as a snapshot (browsable history). Then it
// compares actual YMS arrivals (TRAILER_ARRIVALS_<site>: CHECK_IN by vehicle#,
// IB_DOCK_COMPLETED for density) against the frozen plan: per-bucket above/below,
// unplanned arrivals, per-trailer early/late, per-day execution + density.
//
// Buckets: AP (at port), ODY, RD (redirect), YTA (yet to arrive/new), ON (onsite).

const BUCKETS = ['AP', 'ODY', 'RD', 'YTA', 'ON'];
const BUCKET_LABEL = { AP: 'At Port', ODY: 'ODY', RD: 'Redirect', YTA: 'Yet To Arrive', ON: 'Onsite' };

const TEMPLATE = [
  'TrailerID,Bucket,ExpectedArrivalDate,Vessel',
  '5401869,AP,2026-10-17,MAERSK-SEA-441',    // dummy — At Port
  '8970535,ODY,2026-10-18,MAERSK-SEA-441',   // dummy — off-dock yard, same vessel
  '4320680,RD,2026-10-18,EVER-GIVEN-12',     // dummy — redirect, diff vessel
  '225048,YTA,2026-10-19,EVER-GIVEN-12',     // dummy — yet to arrive
  '5703313,ON,2026-10-16,',                  // dummy — onsite, no vessel noted
].join('\n');

export default function DetailedTrailerPlan({ api, site }) {
  const [arrivals, setArrivals] = useState(null);     // TRAILER_ARRIVALS_<site>
  const [planIndex, setPlanIndex] = useState([]);     // [{id, label, savedAt}]
  const [plan, setPlan] = useState(null);             // active frozen plan
  const [status, setStatus] = useState('');
  const [perDayCartons, setPerDayCartons] = useState({});  // date -> IB cartons
  // Draft (pre-freeze) upload
  const [draftRows, setDraftRows] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [consumePerDay, setConsumePerDay] = useState('27');
  const [assumedDensity, setAssumedDensity] = useState('1250');
  // Burndown inputs
  const [bbPerDay, setBbPerDay] = useState('33750');       // default consumption cartons/day (editable per day below)
  const [bbByDate, setBbByDate] = useState({});            // per-day BB override: date -> cartons
  const todayStr = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
  const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [detailStart, setDetailStart] = useState(todayStr);
  const [detailEnd, setDetailEnd] = useState(() => plusDays(6));
  const [summaryStart, setSummaryStart] = useState(todayStr);
  const [summaryEnd, setSummaryEnd] = useState(() => plusDays(5));

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadArrivals = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=TRAILER_ARRIVALS_${site}&shift_type=data`)
      .then(r => r.json()).then(d => setArrivals(d && Array.isArray(d.checkins) ? d : null)).catch(() => setArrivals(null));
  }, [api, site]);

  const loadPlan = (id) => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=${id}&shift_type=data`)
      .then(r => r.json()).then(d => { if (d && Array.isArray(d.rows)) setPlan(d); }).catch(() => {});
  };

  // On mount: load arrivals (polled) + the plan index, auto-selecting newest.
  useEffect(() => {
    loadArrivals();
    const t = setInterval(loadArrivals, 60000);
    fetch(`${api}/api/fclm/cache?site=${site}&date=TRAILER_PLAN_INDEX&shift_type=data`)
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d?.plans) ? d.plans : [];
        setPlanIndex(list);
        if (list.length) loadPlan(list[list.length - 1].id);   // newest
      })
      .catch(() => setPlanIndex([]));
    return () => clearInterval(t);
  }, [loadArrivals, api, site]);

  // Pull IB cartons/day (MP_WINDOW_<date>.full = {aros,pid}) across the detail
  // and summary date ranges (= BB Processed per day).
  useEffect(() => {
    if (!plan) return;
    const days = new Set();
    const addRange = (s, e) => { const a = new Date(s + 'T12:00:00'), b = new Date(e + 'T12:00:00'); for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) days.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`); };
    addRange(detailStart, detailEnd); addRange(summaryStart, summaryEnd);
    Promise.all([...days].map(ds =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=MP_WINDOW_${ds}&shift_type=data`)
        .then(r => r.json()).then(d => ({ ds, c: (d?.full?.aros || 0) + (d?.full?.pid || 0) })).catch(() => ({ ds, c: 0 }))
    )).then(res => { const m = {}; res.forEach(x => { m[x.ds] = x.c; }); setPerDayCartons(m); });
  }, [plan, api, site, detailStart, detailEnd, summaryStart, summaryEnd]);

  // ── CSV ─────────────────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'detailed-trailer-plan-template.csv'; a.click();
    URL.revokeObjectURL(a.href);
  };

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setDraftName(f.name.replace(/\.csv$/i, ''));
    const reader = new FileReader();
    reader.onload = ev => {
      const lines = ev.target.result.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const rows = [];
      lines.slice(1).forEach(l => {            // skip header
        const [id, bucket, date, vessel] = l.split(',').map(s => (s || '').trim());
        if (!id) return;
        const bk = (bucket || '').toUpperCase();
        rows.push({ trailerId: id, bucket: BUCKETS.includes(bk) ? bk : 'YTA', expected: normDate(date), vessel: vessel || '' });
      });
      setDraftRows(rows);
      setStatus(`Loaded ${rows.length} trailers from ${f.name}. Review and Freeze to save.`);
    };
    reader.readAsText(f);
  };

  const freezePlan = () => {
    if (!draftRows || !draftRows.length) { setStatus('Upload a CSV first.'); return; }
    const now = new Date();
    const id = `TRAILER_PLAN_${site}_${now.getTime()}`;
    const label = `${draftName || 'plan'} — ${now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    const data = { rows: draftRows, consumePerDay: parseFloat(consumePerDay) || 0, assumedDensity: parseFloat(assumedDensity) || 0, savedAt: now.toISOString(), label };
    // Save the plan snapshot, then append to the index.
    fetch(`${api}/api/fclm/cache`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ site, date: id, shift_type: 'data', data }) })
      .then(() => {
        const next = [...planIndex, { id, label, savedAt: data.savedAt }];
        return fetch(`${api}/api/fclm/cache`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ site, date: 'TRAILER_PLAN_INDEX', shift_type: 'data', data: { plans: next } }) })
          .then(() => { setPlanIndex(next); setPlan({ ...data }); setDraftRows(null); setStatus(`Frozen as "${label}".`); });
      })
      .catch(() => setStatus('Freeze failed — try again.'));
  };

  // ── Reconciliation ──────────────────────────────────────────────────────────
  const recon = React.useMemo(() => {
    if (!plan || !arrivals) return null;
    // vehicle# -> earliest check-in ms
    const checkin = {};
    arrivals.checkins.forEach(c => { if (c.vehicle && c.timeMs) { if (!checkin[c.vehicle] || c.timeMs < checkin[c.vehicle]) checkin[c.vehicle] = c.timeMs; } });
    const planIds = new Set(plan.rows.map(r => r.trailerId));

    // Per-bucket planned vs arrived.
    const byBucket = {}; BUCKETS.forEach(b => byBucket[b] = { planned: 0, arrived: 0 });
    const byVessel = {};   // vessel -> {planned, arrived}
    const perTrailer = [];
    plan.rows.forEach(r => {
      byBucket[r.bucket] = byBucket[r.bucket] || { planned: 0, arrived: 0 };
      byBucket[r.bucket].planned += 1;
      const ms = checkin[r.trailerId];
      const arrived = !!ms;
      if (arrived) byBucket[r.bucket].arrived += 1;
      // Per-vessel (only trailers that noted a vessel in the plan).
      if (r.vessel) {
        const v = byVessel[r.vessel] || (byVessel[r.vessel] = { planned: 0, arrived: 0 });
        v.planned += 1; if (arrived) v.arrived += 1;
      }
      let timing = '';
      if (arrived && r.expected) {
        const aDay = pacDay(ms), eDay = normDate(r.expected);
        timing = aDay < eDay ? 'EARLY' : aDay > eDay ? 'LATE' : 'ON TIME';
      }
      perTrailer.push({ ...r, arrived, arrivedDay: ms ? pacDay(ms) : '', timing });
    });
    const vesselRows = Object.keys(byVessel).sort().map(v => ({ vessel: v, ...byVessel[v] }));

    // Unplanned: checked in but not in plan.
    const unplanned = Object.keys(checkin).filter(v => !planIds.has(v)).map(v => ({ trailerId: v, arrivedDay: pacDay(checkin[v]) }));

    // Per-day execution: arrivals/day (planned trailers that arrived that day),
    // IB cartons/day, closed IB containers/day (IB_DOCK_COMPLETED count), actual
    // density = cartons / closed, vs assumed.
    const dockByDay = {};
    (arrivals.dockCompleted || []).forEach(d => { if (d.timeMs) { const k = pacDay(d.timeMs); dockByDay[k] = (dockByDay[k] || 0) + 1; } });
    const arrByDay = {};
    Object.values(checkin).forEach(ms => { const k = pacDay(ms); arrByDay[k] = (arrByDay[k] || 0) + 1; });
    const days = Array.from(new Set([...Object.keys(arrByDay), ...Object.keys(dockByDay), ...Object.keys(perDayCartons)])).sort();
    const perDay = days.map(d => {
      const cartons = perDayCartons[d] || 0;
      const closed = dockByDay[d] || 0;
      const density = closed > 0 ? cartons / closed : null;
      return { day: d, arrived: arrByDay[d] || 0, cartons, closed, density, planConsume: plan.consumePerDay, assumedDensity: plan.assumedDensity };
    });

    return { byBucket, vesselRows, perTrailer, unplanned, perDay };
  }, [plan, arrivals, perDayCartons]);

  // Detailed per-day model: planned burndown + expected-by-bucket (from upload)
  // + actuals (arrivals by bucket, BB processed, actual density, actual EOD).
  // Starting available (SOD of day 1) = count of ON (Onsite) rows in the plan.
  // Editable BB per day lives in bbByDate (keyed date -> cartons), default bbPerDay.
  const detail = React.useMemo(() => {
    if (!plan) return null;
    const dates = [];
    const base = new Date(detailStart + 'T12:00:00');
    const end = new Date(detailEnd + 'T12:00:00');
    for (let d = new Date(base); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    const density = parseFloat(assumedDensity) || 1;
    const defBB = parseFloat(bbPerDay) || 0;

    // Expected (planned) arrivals by bucket/day from the upload.
    const expByDay = {}; dates.forEach(d => { expByDay[d] = { total: 0 }; BUCKETS.forEach(b => expByDay[d][b] = 0); });
    let onsiteStart = 0;
    plan.rows.forEach(r => {
      if (r.bucket === 'ON') onsiteStart += 1;              // ON = starting available
      const ed = normDate(r.expected); if (ed && expByDay[ed]) { expByDay[ed][r.bucket] += 1; expByDay[ed].total += 1; }
    });

    // Actual arrivals by day, split by the trailer's PLANNED bucket (YMS has no
    // bucket) — arrivals not in the plan = Unknown Origin. Dedup by vehicle#.
    const bucketOf = {}; plan.rows.forEach(r => { bucketOf[r.trailerId] = r.bucket; });
    const actByDay = {};
    if (arrivals) {
      const first = {};
      arrivals.checkins.forEach(c => { if (c.vehicle && c.timeMs && (!first[c.vehicle] || c.timeMs < first[c.vehicle])) first[c.vehicle] = c.timeMs; });
      Object.keys(first).forEach(v => {
        const d = pacDay(first[v]);
        if (!actByDay[d]) { actByDay[d] = { total: 0, Unknown: 0 }; BUCKETS.forEach(b => actByDay[d][b] = 0); }
        const bk = bucketOf[v];
        if (bk && bk !== 'ON') actByDay[d][bk] += 1; else if (bk === 'ON') actByDay[d].ON += 1; else actByDay[d].Unknown += 1;
        actByDay[d].total += 1;
      });
    }
    // Actual closed IB containers + BB processed (IB cartons) per day.
    const closedByDay = {};
    (arrivals?.dockCompleted || []).forEach(x => { if (x.timeMs) { const k = pacDay(x.timeMs); closedByDay[k] = (closedByDay[k] || 0) + 1; } });

    // Walk the days: planned burndown and actual burndown both start from ON.
    let planAvail = onsiteStart, actAvail = onsiteStart;
    const rows = dates.map(d => {
      const bb = bbByDate[d] !== undefined ? (parseFloat(bbByDate[d]) || 0) : defBB;
      const consumeC = density > 0 ? Math.round(bb / density) : 0;
      const exp = expByDay[d] || { total: 0 };
      const planSod = planAvail; planAvail = planAvail - consumeC + exp.total; const planEod = planAvail;

      const act = actByDay[d] || null;
      const bbProc = perDayCartons[d] || 0;              // actual IB cartons
      const closed = closedByDay[d] || 0;                // actual consumption (containers)
      const actDensity = closed > 0 ? Math.round(bbProc / closed) : null;
      const actArr = act ? act.total : 0;
      const actSod = actAvail; actAvail = actAvail - closed + actArr; const actEod = actAvail;

      return { date: d, bb, consumeC, exp, planSod, planEod, act, bbProc, closed, actDensity, actEod };
    });
    return { dates, rows, onsiteStart, density };
  }, [plan, arrivals, perDayCartons, detailStart, detailEnd, assumedDensity, bbPerDay, bbByDate]);

  // Summary roll-up over its own [summaryStart, summaryEnd]: Expected/Actual/Delta.
  const summary = React.useMemo(() => {
    if (!plan) return null;
    const inRange = (d) => d >= summaryStart && d <= summaryEnd;
    const density = parseFloat(assumedDensity) || 1;
    const defBB = parseFloat(bbPerDay) || 0;
    // Expected arrivals by bucket (planned, expected date in range).
    const exp = { total: 0 }; BUCKETS.forEach(b => exp[b] = 0); exp.Unknown = 0;
    const bucketOf = {}; plan.rows.forEach(r => { bucketOf[r.trailerId] = r.bucket; });
    plan.rows.forEach(r => { const ed = normDate(r.expected); if (ed && inRange(ed) && r.bucket !== 'ON') { exp[r.bucket] += 1; exp.total += 1; } });
    // Actual arrivals by (planned) bucket, check-in in range.
    const act = { total: 0 }; BUCKETS.forEach(b => act[b] = 0); act.Unknown = 0;
    if (arrivals) {
      const first = {}; arrivals.checkins.forEach(c => { if (c.vehicle && c.timeMs && (!first[c.vehicle] || c.timeMs < first[c.vehicle])) first[c.vehicle] = c.timeMs; });
      Object.keys(first).forEach(v => { const d = pacDay(first[v]); if (!inRange(d)) return; const bk = bucketOf[v]; if (bk && bk !== 'ON') act[bk] += 1; else if (!bk) act.Unknown += 1; act.total += 1; });
    }
    // Days in range (for expected consumption).
    let nDays = 0; { const s = new Date(summaryStart + 'T12:00:00'), e = new Date(summaryEnd + 'T12:00:00'); for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) nDays++; }
    const expConsumed = density > 0 ? Math.round((defBB * nDays) / density) : 0;
    // Actual consumed = closed IB containers in range; actual density = BB processed / closed.
    let actConsumed = 0, actBB = 0;
    (arrivals?.dockCompleted || []).forEach(x => { if (x.timeMs && inRange(pacDay(x.timeMs))) actConsumed += 1; });
    Object.keys(perDayCartons).forEach(d => { if (inRange(d)) actBB += perDayCartons[d]; });
    const actDensity = actConsumed > 0 ? Math.round(actBB / actConsumed) : null;
    return { exp, act, expConsumed, actConsumed, expDensity: Math.round(density), actDensity, nDays };
  }, [plan, arrivals, perDayCartons, summaryStart, summaryEnd, assumedDensity, bbPerDay]);

  // ── styles ───────────────────────────────────────────────────────────────────
  const hdr = { background: '#232f3e', color: '#fff', border: '1px solid #ccc', padding: '3px 8px', fontSize: '0.72rem', textAlign: 'left' };
  const cell = { border: '1px solid #eee', padding: '2px 8px', fontSize: '0.74rem' };
  const num = { ...cell, textAlign: 'right' };

  return (
    <div style={{ padding: 12 }}>
      <h2 className="section-title">Detailed Trailer Plan — {site}</h2>

      {/* Plan upload / freeze / history */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn" style={btn} onClick={downloadTemplate}>⬇ Download template</button>
          <label style={btn}>📄 Upload CSV<input type="file" accept=".csv" onChange={onFile} style={{ display: 'none' }} /></label>
          <label style={{ fontSize: '0.72rem' }}>Consume/day <input value={consumePerDay} onChange={e => setConsumePerDay(e.target.value)} style={inp} /></label>
          <label style={{ fontSize: '0.72rem' }}>Assumed density <input value={assumedDensity} onChange={e => setAssumedDensity(e.target.value)} style={inp} /></label>
          {draftRows && <button className="btn btn-primary" style={{ ...btn, background: '#00b894', color: '#fff' }} onClick={freezePlan}>❄ Freeze plan ({draftRows.length})</button>}
          {planIndex.length > 0 && (
            <label style={{ fontSize: '0.72rem' }}>View plan{' '}
              <select value={plan?.label ? planIndex.find(p => p.label === plan.label)?.id || '' : ''} onChange={e => loadPlan(e.target.value)} style={{ fontSize: '0.72rem' }}>
                {planIndex.slice().reverse().map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
          )}
        </div>
        {status && <p style={{ fontSize: '0.72rem', color: '#555', margin: '6px 0 0' }}>{status}</p>}
        {arrivals?.generatedAt && <p style={{ fontSize: '0.62rem', color: '#999', margin: '2px 0 0' }}>Arrivals as of {new Date(arrivals.generatedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.</p>}
        {/* Acronym / column legend for the CSV upload */}
        <div style={{ marginTop: 8, padding: '6px 10px', background: '#f7f9fb', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: '0.66rem', color: '#444', lineHeight: 1.5 }}>
          <strong>CSV columns:</strong> <code>TrailerID</code> = Vehicle # (matches YMS check-in) · <code>Bucket</code> · <code>ExpectedArrivalDate</code> = YYYY-MM-DD · <code>Vessel</code> = your vessel name/ID (optional).
          <br /><strong>Bucket codes:</strong>{' '}
          <b>AP</b> = At Port · <b>ODY</b> = Off-Dock Yard · <b>RD</b> = Redirect · <b>YTA</b> = Yet To Arrive (new at port) · <b>ON</b> = Onsite.
        </div>
        {/* Burndown inputs */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>Detailed table:</span>
          <label style={{ fontSize: '0.72rem' }}>start <input type="date" value={detailStart} onChange={e => setDetailStart(e.target.value)} style={{ fontSize: '0.72rem' }} /></label>
          <label style={{ fontSize: '0.72rem' }}>end <input type="date" value={detailEnd} onChange={e => setDetailEnd(e.target.value)} style={{ fontSize: '0.72rem' }} /></label>
          <span style={{ fontSize: '0.72rem', fontWeight: 600, marginLeft: 12 }}>Summary:</span>
          <label style={{ fontSize: '0.72rem' }}>start <input type="date" value={summaryStart} onChange={e => setSummaryStart(e.target.value)} style={{ fontSize: '0.72rem' }} /></label>
          <label style={{ fontSize: '0.72rem' }}>end <input type="date" value={summaryEnd} onChange={e => setSummaryEnd(e.target.value)} style={{ fontSize: '0.72rem' }} /></label>
          <label style={{ fontSize: '0.72rem', marginLeft: 12 }}>Default BB/day <input value={bbPerDay} onChange={e => setBbPerDay(e.target.value)} style={{ ...inp, width: 70 }} /></label>
          <label style={{ fontSize: '0.72rem' }}>Density <input value={assumedDensity} onChange={e => setAssumedDensity(e.target.value)} style={inp} /></label>
          {detail && <span style={{ fontSize: '0.7rem', color: '#555' }}>· starting available (ON) = {detail.onsiteStart}</span>}
        </div>
      </div>

      {/* ── SUMMARY table (own date range): Expected / Actual / Delta ── */}
      {plan && summary && (
        <div className="card" style={{ marginBottom: 10 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 2px' }}>Summary <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>· {summaryStart} → {summaryEnd} ({summary.nDays}d)</span></h3>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead><tr><th style={hdr}></th><th style={{ ...hdr, textAlign: 'right' }}>Expected</th><th style={{ ...hdr, textAlign: 'right' }}>Actual</th><th style={{ ...hdr, textAlign: 'right' }}>Delta</th></tr></thead>
            <tbody>
              {BUCKETS.filter(b => b !== 'ON').map(b => {
                const d = (summary.act[b] || 0) - (summary.exp[b] || 0);
                return <tr key={b}><td style={cell}>Arrivals from {b}</td><td style={num}>{summary.exp[b] || 0}</td><td style={num}>{summary.act[b] || 0}</td><td style={{ ...num, fontWeight: 600, color: d > 0 ? '#2e7d32' : d < 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td></tr>;
              })}
              {(() => { const d = (summary.act.Unknown || 0) - 0; return <tr><td style={cell}>Unknown Origin</td><td style={num}>0</td><td style={num}>{summary.act.Unknown || 0}</td><td style={{ ...num, fontWeight: 600, color: d > 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td></tr>; })()}
              {(() => { const d = (summary.act.total || 0) - (summary.exp.total || 0); return <tr style={{ borderTop: '2px solid #999' }}><td style={{ ...cell, fontWeight: 700 }}>Total Arrivals</td><td style={{ ...num, fontWeight: 700 }}>{summary.exp.total || 0}</td><td style={{ ...num, fontWeight: 700 }}>{summary.act.total || 0}</td><td style={{ ...num, fontWeight: 700, color: d > 0 ? '#2e7d32' : d < 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td></tr>; })()}
              <tr><td style={cell}>Trailer Density</td><td style={num}>{summary.expDensity}</td><td style={num}>{summary.actDensity != null ? summary.actDensity : '—'}</td><td style={{ ...num, fontWeight: 600 }}>{summary.actDensity != null ? (summary.actDensity - summary.expDensity > 0 ? '+' : '') + (summary.actDensity - summary.expDensity) : ''}</td></tr>
              {(() => { const d = (summary.actConsumed || 0) - (summary.expConsumed || 0); return <tr><td style={cell}>Trailers Consumed</td><td style={num}>{summary.expConsumed}</td><td style={num}>{summary.actConsumed}</td><td style={{ ...num, fontWeight: 600, color: d > 0 ? '#2e7d32' : d < 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td></tr>; })()}
            </tbody>
          </table>
        </div>
      )}

      {/* ── DETAILED table (own date range): per-day burndown + expected + actual ── */}
      {plan && detail && (() => {
        const thS = { background: '#232f3e', color: '#fff', border: '1px solid #ccc', padding: '2px 6px', fontSize: '0.64rem', textAlign: 'center' };
        const tdS = { border: '1px solid #eee', padding: '2px 6px', fontSize: '0.66rem', textAlign: 'center' };
        const lbl = { ...tdS, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', minWidth: 180 };
        const blue = '#dbe9ff', red = '#fde3d8';
        const dow = (d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d + 'T12:00:00').getDay()];
        const R = (rows) => rows;
        return (
          <div className="card" style={{ marginBottom: 10, overflowX: 'auto' }}>
            <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Detailed — Daily Plan vs Actual <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>· {detailStart} → {detailEnd}</span></h3>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...thS, textAlign: 'left', minWidth: 180 }}>Day</th>{detail.rows.map(r => <th key={r.date} style={thS}>{dow(r.date)} {r.date.slice(5)}</th>)}</tr></thead>
              <tbody>
                {/* Editable BB (blue) */}
                <tr><td style={{ ...lbl, background: blue }}>Consumption (BB cartons)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, background: blue, padding: 1 }}><input value={bbByDate[r.date] !== undefined ? bbByDate[r.date] : r.bb} onChange={e => setBbByDate(p => ({ ...p, [r.date]: e.target.value }))} style={{ width: 54, fontSize: '0.64rem', textAlign: 'center', border: '1px solid #9bc', background: blue }} /></td>)}</tr>
                <tr><td style={{ ...lbl, background: blue }}>Consumption (containers)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, background: blue }}>{r.consumeC}</td>)}</tr>
                <tr><td style={lbl}>Total Available (SOD)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 600, color: r.planSod < 0 ? '#c62828' : '#333' }}>{Math.round(r.planSod)}</td>)}</tr>
                <tr style={{ borderBottom: '2px solid #bbb' }}><td style={lbl}>Total Available (EOD)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 700, color: r.planEod < 0 ? '#c62828' : '#1b5e20' }}>{Math.round(r.planEod)}</td>)}</tr>
                {/* Expected by bucket (red = from upload) */}
                {BUCKETS.map(b => <tr key={b}><td style={{ ...lbl, background: red }}>Expected: {b} ({BUCKET_LABEL[b]})</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, background: red }}>{b === 'ON' ? (r.date === detailStart ? detail.onsiteStart : '-') : (r.exp[b] || '')}</td>)}</tr>)}
                <tr style={{ borderBottom: '2px solid #bbb' }}><td style={lbl}>Expected arrivals (total)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 600 }}>{r.exp.total || ''}</td>)}</tr>
                {/* Actual arrivals */}
                <tr><td style={{ ...lbl, fontWeight: 700 }}>Actual Arrivals Total</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 700 }}>{r.act ? r.act.total : ''}</td>)}</tr>
                {['AP', 'ODY', 'RD', 'YTA'].map(b => <tr key={b}><td style={lbl}>{b}</td>{detail.rows.map(r => <td key={r.date} style={tdS}>{r.act ? (r.act[b] || '') : ''}</td>)}</tr>)}
                <tr style={{ borderBottom: '2px solid #bbb' }}><td style={lbl}>Unknown Origin</td>{detail.rows.map(r => <td key={r.date} style={tdS}>{r.act ? (r.act.Unknown || '') : ''}</td>)}</tr>
                {/* Actual consumption / density */}
                <tr><td style={lbl}>Actual Consumption (containers)</td>{detail.rows.map(r => <td key={r.date} style={tdS}>{r.closed || ''}</td>)}</tr>
                <tr><td style={lbl}>BB Processed</td>{detail.rows.map(r => <td key={r.date} style={tdS}>{r.bbProc ? r.bbProc.toLocaleString() : ''}</td>)}</tr>
                <tr style={{ borderBottom: '2px solid #bbb' }}><td style={lbl}>Actual Container Density</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 600 }}>{r.actDensity != null ? r.actDensity.toLocaleString() : ''}</td>)}</tr>
                {/* Actual burndown */}
                <tr><td style={{ ...lbl, fontWeight: 700 }}>Actual Total Available (EOD)</td>{detail.rows.map(r => <td key={r.date} style={{ ...tdS, fontWeight: 700, color: r.actEod < 0 ? '#c62828' : '#1b5e20' }}>{Math.round(r.actEod)}</td>)}</tr>
              </tbody>
            </table>
            <p style={{ fontSize: '0.62rem', color: '#999', marginTop: 4 }}>Blue = editable · Red = from your upload · Starting available (SOD day 1) = ON count ({detail.onsiteStart}). Actual arrivals bucketed by the trailer's planned tag; unplanned = Unknown Origin. {!arrivals && 'Actuals blank until the trailer-arrivals sync runs.'}</p>
          </div>
        );
      })()}


      {!plan ? (
        <p style={{ fontSize: '0.8rem', color: '#888' }}>Download the template, fill it in, upload, and Freeze a plan to begin reconciliation.</p>
      ) : !recon ? (
        <p style={{ fontSize: '0.8rem', color: '#888' }}>Waiting for arrivals data (TRAILER_ARRIVALS)…</p>
      ) : (
        <>
          {/* Per-bucket */}
          <div className="card" style={{ marginBottom: 10 }}>
            <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Per Bucket — Planned vs Arrived</h3>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr><th style={hdr}>Bucket</th><th style={hdr}>Planned</th><th style={hdr}>Arrived</th><th style={hdr}>Δ</th></tr></thead>
              <tbody>
                {BUCKETS.map(b => {
                  const x = recon.byBucket[b] || { planned: 0, arrived: 0 };
                  const d = x.arrived - x.planned;
                  return (
                    <tr key={b}>
                      <td style={{ ...cell, fontWeight: 600 }}>{b} <span style={{ color: '#999', fontWeight: 400 }}>({BUCKET_LABEL[b]})</span></td>
                      <td style={num}>{x.planned}</td>
                      <td style={num}>{x.arrived}</td>
                      <td style={{ ...num, fontWeight: 600, color: d > 0 ? '#2e7d32' : d < 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Per vessel */}
          {recon.vesselRows.length > 0 && (
            <div className="card" style={{ marginBottom: 10 }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Per Vessel — Trailers Planned vs Arrived</h3>
              <table style={{ borderCollapse: 'collapse' }}>
                <thead><tr><th style={hdr}>Vessel</th><th style={hdr}>Planned</th><th style={hdr}>Arrived</th><th style={hdr}>Δ</th></tr></thead>
                <tbody>
                  {recon.vesselRows.map(v => {
                    const d = v.arrived - v.planned;
                    return (
                      <tr key={v.vessel}>
                        <td style={{ ...cell, fontWeight: 600 }}>{v.vessel}</td>
                        <td style={num}>{v.planned}</td>
                        <td style={num}>{v.arrived}</td>
                        <td style={{ ...num, fontWeight: 600, color: d > 0 ? '#2e7d32' : d < 0 ? '#c0392b' : '#666' }}>{d > 0 ? `+${d}` : d}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: '0.62rem', color: '#999', marginTop: 4 }}>Only trailers that noted a Vessel in the plan are counted here.</p>
            </div>
          )}

          {/* Unplanned */}
          <div className="card" style={{ marginBottom: 10 }}>
            <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Unplanned Arrivals <span style={{ fontWeight: 400, fontSize: '0.7rem', color: '#c0392b' }}>({recon.unplanned.length} not in plan)</span></h3>
            {recon.unplanned.length === 0 ? <p style={{ fontSize: '0.74rem', color: '#888' }}>None — every arrival was planned.</p> : (
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr><th style={hdr}>Trailer (Vehicle#)</th><th style={hdr}>Arrived</th></tr></thead>
                  <tbody>{recon.unplanned.map(u => <tr key={u.trailerId} style={{ background: '#fdecea' }}><td style={cell}>{u.trailerId}</td><td style={cell}>{u.arrivedDay}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </div>

          {/* Per day execution + density */}
          <div className="card" style={{ marginBottom: 10 }}>
            <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Per Day — Execution vs Plan</h3>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>{['Day', 'Arrived', 'Consume Plan', 'IB Cartons', 'Closed IB', 'Density', 'Assumed', 'vs Plan'].map(h => <th key={h} style={hdr}>{h}</th>)}</tr></thead>
              <tbody>
                {recon.perDay.map(d => {
                  const dd = d.density;
                  const diff = dd != null && d.assumedDensity ? dd - d.assumedDensity : null;
                  return (
                    <tr key={d.day}>
                      <td style={{ ...cell, fontWeight: 600 }}>{d.day}</td>
                      <td style={num}>{d.arrived}</td>
                      <td style={num}>{d.planConsume || ''}</td>
                      <td style={num}>{d.cartons ? d.cartons.toLocaleString() : ''}</td>
                      <td style={num}>{d.closed || ''}</td>
                      <td style={{ ...num, fontWeight: 600 }}>{dd != null ? Math.round(dd).toLocaleString() : '—'}</td>
                      <td style={num}>{d.assumedDensity || ''}</td>
                      <td style={{ ...num, fontWeight: 600, color: diff == null ? '#999' : diff > 0 ? '#2e7d32' : '#c0392b' }}>{diff == null ? '' : (diff > 0 ? '▲ ' : '▼ ') + Math.abs(Math.round(diff))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p style={{ fontSize: '0.62rem', color: '#999', marginTop: 4 }}>Density = IB cartons ÷ closed IB containers (IB_DOCK_COMPLETED). ▲ above / ▼ below your assumed density.</p>
          </div>

          {/* Per trailer early/late */}
          <div className="card">
            <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Per Trailer — Timing</h3>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>{['Trailer', 'Bucket', 'Vessel', 'Expected', 'Arrived', 'Timing'].map(h => <th key={h} style={hdr}>{h}</th>)}</tr></thead>
                <tbody>
                  {recon.perTrailer.map(t => (
                    <tr key={t.trailerId} style={{ background: !t.arrived ? '#fff8e1' : t.timing === 'LATE' ? '#fdecea' : t.timing === 'EARLY' ? '#eafaf1' : 'transparent' }}>
                      <td style={cell}>{t.trailerId}</td>
                      <td style={cell}>{t.bucket}</td>
                      <td style={cell}>{t.vessel || ''}</td>
                      <td style={cell}>{t.expected}</td>
                      <td style={cell}>{t.arrivedDay || <span style={{ color: '#f57f17' }}>not yet</span>}</td>
                      <td style={{ ...cell, fontWeight: 600, color: t.timing === 'LATE' ? '#c0392b' : t.timing === 'EARLY' ? '#2e7d32' : '#666' }}>{t.arrived ? t.timing : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Pacific calendar day (YYYY-MM-DD) from epoch ms.
// Normalize an uploaded date to YYYY-MM-DD. Accepts M/D/YYYY, MM/DD/YYYY, or
// an already-correct YYYY-MM-DD (so the grid's date keys match).
function normDate(s) {
  s = (s || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;                 // already ISO
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);        // M/D/YYYY
  if (m) return `${m[3]}-${String(+m[1]).padStart(2, '0')}-${String(+m[2]).padStart(2, '0')}`;
  const m2 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);       // M/D/YY
  if (m2) return `20${m2[3]}-${String(+m2[1]).padStart(2, '0')}-${String(+m2[2]).padStart(2, '0')}`;
  const d = new Date(s); if (!isNaN(d)) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return s;
}
function pacDay(ms) {
  const d = new Date(new Date(ms).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const btn = { fontSize: '0.72rem', padding: '5px 10px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: '#f5f5f5' };
const inp = { fontSize: '0.72rem', width: 60, padding: '3px 6px', border: '1px solid #ccc', borderRadius: 4, marginLeft: 4 };
